import { acceptHMRUpdate, defineStore } from 'pinia'
import { useFieldProblemsStore } from './fieldProblems'
import { useDrawingStore } from './drawing'
import { useMaps } from './maps'

export const useSvgStore = defineStore('svg', {
    state: () => ({
        zoomLevel: 1,
        minZoom: 0.1,
        maxZoom: 2000000,
        panX: 0,
        panY: 0,
        normalizedCoordinates: [],
        baseSize: 200,
        isPanning: false,
        lastMouseX: 0,
        lastMouseY: 0,
        baseHeight: 100,
        debouncedZoomLevel: 1,
        scaleUpdateTimer: null,
        editMode: false,
        selectedVertexIndex: -1,
        originalCoordinates: null,
        modifiedCoordinates: null,
        hasChanges: false,
        problemPoint: null,
        vertexToDeleteIndex: -1,
        showDeleteConfirm: false,
        normalizedObstacles: [],
        selectedObstacleIndex: -1,
        selectedObstacleVertexIndex: -1,
        editingObstacle: false,
        editingField: false,
        obstacleBackup: null,
        fixedBoundingBox: null // bounding box fix for normalization in edit mode
    }),
    getters: {
        polylinePoints() {
            return this.normalizedCoordinates
                .map(coord => `${coord.x},${coord.y}`)
                .join(' ')
        },
        tempOuterBorderPoints() {
            const normalizedData = this.getNormalizedTempOuterBorder()
            if (!normalizedData) return null
            
            const { coordinates, minX, minY, width, height, viewBoxWidth, viewBoxHeight } = normalizedData
            
            return coordinates.map(coord => ({
                x: ((coord[0] - minX) / width) * viewBoxWidth,
                y: viewBoxHeight - ((coord[1] - minY) / height) * viewBoxHeight
            })).map(coord => `${coord.x},${coord.y}`).join(' ')
        },
        tempOuterBorderCoordinates() {
            const normalizedData = this.getNormalizedTempOuterBorder()
            if (!normalizedData) return []
            
            const { coordinates, minX, minY, width, height, viewBoxWidth, viewBoxHeight } = normalizedData
            
            return coordinates.map(coord => ({
                x: ((coord[0] - minX) / width) * viewBoxWidth,
                y: viewBoxHeight - ((coord[1] - minY) / height) * viewBoxHeight
            }))
        },
        tempObstaclePoints() {
            const fieldProblemsStore = useFieldProblemsStore()
            if (!fieldProblemsStore.tempOuterBorder) return []
            
            // Get normalized data for field to use same scale
            const normalizedFieldData = this.getNormalizedTempOuterBorder()
            if (!normalizedFieldData) return []
            
            const { minX, minY, width, height, viewBoxWidth, viewBoxHeight } = normalizedFieldData
            
            // Extract obstacle rings from the suggested geometry
            const innerRings = this.extractInnerRings(fieldProblemsStore.tempOuterBorder.outer_border)
            
            // Transform each inner ring to SVG coordinates and create a points string
            return innerRings.map(ring => {
                const normalizedRing = ring.map(coord => ({
                    x: ((coord[0] - minX) / width) * viewBoxWidth, 
                    y: viewBoxHeight - ((coord[1] - minY) / height) * viewBoxHeight
                }))
                
                return {
                    points: normalizedRing.map(coord => `${coord.x},${coord.y}`).join(' '),
                    coordinates: normalizedRing
                }
            })
        },
        getScaleText() {
            // Use the fixed bounding box if it exists
            let coords
            if (this.fixedBoundingBox && this.originalCoordinates && this.originalCoordinates.length > 1) {
                coords = this.originalCoordinates.map(c => c.originalCoord || c)
            } else if (this.normalizedCoordinates && this.normalizedCoordinates.length > 1) {
                coords = this.normalizedCoordinates.map(c => c.originalCoord || c)
            } else {
                return "0 m"
            }

            let maxDistance = 0
            for (let i = 0; i < coords.length; i++) {
                for (let j = i + 1; j < coords.length; j++) {
                    const coord1 = coords[i]
                    const coord2 = coords[j]
                    const R = 6371000
                    const lat1 = coord1[1] * Math.PI / 180
                    const lat2 = coord2[1] * Math.PI / 180
                    const deltaLat = (coord2[1] - coord1[1]) * Math.PI / 180
                    const deltaLon = (coord2[0] - coord1[0]) * Math.PI / 180
                    const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
                            Math.cos(lat1) * Math.cos(lat2) *
                            Math.sin(deltaLon/2) * Math.sin(deltaLon/2)
                    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
                    const distance = R * c
                    maxDistance = Math.max(maxDistance, distance)
                }
            }
            const adjustedDistance = Math.round((maxDistance / this.debouncedZoomLevel) * 100) / 100
            if (adjustedDistance >= 1000) {
                return `${Math.round(adjustedDistance / 100) / 10} km`
            } else if (adjustedDistance >= 1) {
                return `${Math.round(adjustedDistance)} m`
            } else {
                return `${Math.round(adjustedDistance * 100)} cm`
            }
        }
    },
    actions: {
        generateSVG() {
            this.updateCoordinates()
        },
        normalizeCoordinates(coordinates) {
            if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) return null
            
            const minX = Math.min(...coordinates.map(coord => coord[0]))
            const minY = Math.min(...coordinates.map(coord => coord[1]))
            const maxX = Math.max(...coordinates.map(coord => coord[0]))
            const maxY = Math.max(...coordinates.map(coord => coord[1]))

            const buffer = 0.00001
            const width = Math.max(maxX - minX, buffer)
            const height = Math.max(maxY - minY, buffer)

            const aspectRatio = width / height
            let viewBoxWidth = this.baseSize
            let viewBoxHeight = this.baseSize

            if (aspectRatio > 1) {
                viewBoxHeight = viewBoxWidth / aspectRatio
            } else {
                viewBoxWidth = viewBoxHeight * aspectRatio
            }

            return {
                coordinates,
                minX,
                minY,
                width,
                height,
                viewBoxWidth,
                viewBoxHeight
            }
        },
        getNormalizedTempOuterBorder() {
            const fieldProblemsStore = useFieldProblemsStore()

            if (!fieldProblemsStore.tempOuterBorder) return null
            
            const coordinates = this.extractCoordinatesFromGeometry(fieldProblemsStore.tempOuterBorder.outer_border)
            if (!coordinates) return null

            // Use the fixed bounding box if it exists
            if (this.fixedBoundingBox) {
                const bbox = this.fixedBoundingBox
                return {
                    coordinates,
                    minX: bbox.minX,
                    minY: bbox.minY,
                    width: bbox.width,
                    height: bbox.height,
                    viewBoxWidth: bbox.viewBoxWidth,
                    viewBoxHeight: bbox.viewBoxHeight
                }
            }

            // Use the same normalization as the field coordinates if they exist
            if (this.normalizedCoordinates && this.normalizedCoordinates.length > 0) {
                const fieldCoords = this.normalizedCoordinates.map(coord => coord.originalCoord)
                const fieldNormalization = this.normalizeCoordinates(fieldCoords)
                if (!fieldNormalization) return null
                
                // Return the temp coordinates normalized with field's parameters
                return {
                    coordinates,
                    minX: fieldNormalization.minX,
                    minY: fieldNormalization.minY,
                    width: fieldNormalization.width,
                    height: fieldNormalization.height,
                    viewBoxWidth: fieldNormalization.viewBoxWidth,
                    viewBoxHeight: fieldNormalization.viewBoxHeight
                }
            }

            return this.normalizeCoordinates(coordinates)
        },
        isPointProblem(coord) {
            const fieldProblemsStore = useFieldProblemsStore()
            
            if (!fieldProblemsStore.problems.coord) return false
            
            return fieldProblemsStore.problems.coord.some(problemCoord => {
                // Check if the coordinates coincide (with a small margin of error for floating point numbers)
                const epsilon = 0.000001
                return Math.abs(problemCoord[0] - coord[0]) < epsilon && 
                       Math.abs(problemCoord[1] - coord[1]) < epsilon
            })
        },
        extractCoordinatesFromGeometry(geometry) {
            if (!geometry) return null
            
            let coordinates = null
            
            if (geometry.type === 'FeatureCollection' && 
                geometry.features && 
                geometry.features[0] && 
                geometry.features[0].geometry && 
                geometry.features[0].geometry.coordinates) {
                
                coordinates = geometry.features[0].geometry.coordinates[0]
            } else if (geometry.type === 'Polygon' && 
                    geometry.coordinates && 
                    geometry.coordinates[0]) {
                
                coordinates = geometry.coordinates[0]
            }
            
            return coordinates
        },
        extractInnerRings(geometry) {
            if (!geometry) return []
            
            let innerRings = []
            
            if (geometry.type === 'FeatureCollection' && 
                geometry.features && 
                geometry.features[0] && 
                geometry.features[0].geometry && 
                geometry.features[0].geometry.coordinates) {
                
                // Skip the first array (outer ring) and get all inner rings
                if (geometry.features[0].geometry.coordinates.length > 1) {
                    innerRings = geometry.features[0].geometry.coordinates.slice(1)
                }
                
            } else if (geometry.type === 'Polygon' && 
                    geometry.coordinates) {
                
                // Skip the first array (outer ring) and get all inner rings
                if (geometry.coordinates.length > 1) {
                    innerRings = geometry.coordinates.slice(1)
                }
            }
            
            return innerRings
        },
        updateCoordinates() {
            const drawingStore = useDrawingStore()

            if (!drawingStore.field.outerBorder) return

            let coordinates = null

            if (drawingStore.field.requestHeadlandData && 
                drawingStore.field.requestHeadlandData.outer_border) {
                coordinates = this.extractCoordinatesFromGeometry(drawingStore.field.requestHeadlandData.outer_border)
            } else {
                coordinates = this.extractCoordinatesFromGeometry(drawingStore.field.outerBorder)
            }

            if (!coordinates) return
            
            const normalizedData = this.normalizeCoordinates(coordinates)
            if (!normalizedData) return
            
            const { minX, minY, width, height, viewBoxWidth, viewBoxHeight } = normalizedData
            
            this.normalizedCoordinates = coordinates.map(coord => ({
                x: ((coord[0] - minX) / width) * viewBoxWidth,
                y: viewBoxHeight - ((coord[1] - minY) / height) * viewBoxHeight,
                originalCoord: coord
            }))
            
            this.updateObstacleCoordinates()
        },
        updateObstacleCoordinates() {
            const drawingStore = useDrawingStore()
            
            if (!this.normalizedCoordinates || this.normalizedCoordinates.length === 0) return
            
            const fieldCoords = this.normalizedCoordinates.map(coord => coord.originalCoord)
            // Use the same normalization as for the field coordinates
            const normalizedData = this.normalizeCoordinates(fieldCoords)
            const { minX, minY, width, height, viewBoxWidth, viewBoxHeight } = normalizedData
            
            this.normalizedObstacles = []
            
            for (const obstacle of drawingStore.field.cutouts) {
                if (!obstacle.getPath) continue
                
                const obstacleCoords = obstacle.getPath().getArray().map(coord => [coord.lng(), coord.lat()])
                
                const normalizedObstacleCoords = obstacleCoords.map(coord => ({
                    x: ((coord[0] - minX) / width) * viewBoxWidth,
                    y: viewBoxHeight - ((coord[1] - minY) / height) * viewBoxHeight,
                    originalCoord: coord
                }))
                
                this.normalizedObstacles.push({
                    id: obstacle.id || `obstacle-${this.normalizedObstacles.length}`,
                    coordinates: normalizedObstacleCoords,
                    points: normalizedObstacleCoords.map(coord => `${coord.x},${coord.y}`).join(' ')
                })
            }
        },
        cancelObstacleChanges() {
            // Restore from backup
            if (this.obstacleBackup) {
                this.normalizedObstacles = JSON.parse(JSON.stringify(this.obstacleBackup))
            }
            
            this.editingObstacle = false
            this.selectedObstacleIndex = -1
            this.selectedObstacleVertexIndex = -1
            this.obstacleBackup = null
            this.hasChanges = false
        },
        startPan(event) {
            this.isPanning = true
            this.lastMouseX = event.clientX
            this.lastMouseY = event.clientY
        },
        pan(event, svgRect) {
            if (!this.isPanning) return

            const dx = event.clientX - this.lastMouseX
            const dy = event.clientY - this.lastMouseY
            
            const scaleX = this.baseSize / svgRect.width / this.zoomLevel
            const scaleY = this.baseSize / svgRect.height / this.zoomLevel

            this.panX += dx * scaleX
            this.panY += dy * scaleY

            this.lastMouseX = event.clientX
            this.lastMouseY = event.clientY
        },
        stopPan() {
            this.isPanning = false
        },
        handleZoom(event, svgRect) {
            const oldZoom = this.zoomLevel
            const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1
            const newZoom = oldZoom * zoomFactor

            if (newZoom >= this.minZoom && newZoom <= this.maxZoom) {
                
                const mouseXNorm = (event.clientX - svgRect.left) / svgRect.width
                const mouseYNorm = (event.clientY - svgRect.top) / svgRect.height
                
                const mouseXSvg = this.baseSize * mouseXNorm
                const mouseYSvg = this.baseSize * mouseYNorm

                const worldX = (mouseXSvg / oldZoom) - this.panX
                const worldY = (mouseYSvg / oldZoom) - this.panY

                this.panX = -(worldX - (mouseXSvg / newZoom))
                this.panY = -(worldY - (mouseYSvg / newZoom))

                this.zoomLevel = newZoom
                
                if (this.scaleUpdateTimer) {
                    clearTimeout(this.scaleUpdateTimer)
                }
                this.scaleUpdateTimer = setTimeout(() => {
                    this.debouncedZoomLevel = this.zoomLevel
                }, 500)
            }
        },
        updateSvgCenter(latLng) {
            // Find the normalized coordinate that corresponds to the marker position
            if (!this.normalizedCoordinates || this.normalizedCoordinates.length === 0) {
                return
            }

            // Get the field coordinates to use for normalization
            const fieldCoords = this.normalizedCoordinates.map(coord => coord.originalCoord)
            const normalizedData = this.normalizeCoordinates(fieldCoords)
            if (!normalizedData) return
            
            const { minX, minY, width, height, viewBoxWidth, viewBoxHeight } = normalizedData

            // Convert the marker position to SVG coordinates using the same normalization
            const markerX = ((latLng.lng - minX) / width) * viewBoxWidth
            const markerY = viewBoxHeight - ((latLng.lat - minY) / height) * viewBoxHeight

            this.problemPoint = {
                x: markerX,
                y: markerY
            }

            // Set zoom level
            // Latitude: 1 degree = 111,000 meters approximately at the equator
            const latDegreeInMeters = 111000
            const scaleMultiplier = 0.000000108
            // Scale multiplier reference values:
            // For 2 meters: 0.00000086
            // For 1 meter: 0.00000043
            // For 0.5 meters: 0.000000215
            // For 0.25 meters: 0.000000108
            const targetZoom = this.baseSize / (latDegreeInMeters * scaleMultiplier)
            this.zoomLevel = Math.min(Math.max(targetZoom, this.minZoom), this.maxZoom)
            this.debouncedZoomLevel = this.zoomLevel

            // Center the view on the problem point
            this.panX = -markerX + this.baseSize / (2 * this.zoomLevel)
            this.panY = -markerY + this.baseSize / (2 * this.zoomLevel)
        },
        setEditMode(enabled) {
            // If we are disabling edit mode, save the changes to the obstacle
            if (!enabled && this.editMode && this.editingObstacle && this.hasChanges) {
                this.saveObstacleChanges()
            }
            
            this.editMode = enabled
            
            if (enabled) {
                // Store original coordinates for backup
                this.originalCoordinates = JSON.parse(JSON.stringify(this.normalizedCoordinates))
                this.modifiedCoordinates = JSON.parse(JSON.stringify(this.normalizedCoordinates))
                this.hasChanges = false
                
                // Hide problem point in edit mode
                this.problemPoint = null
                // Save the fixed bounding box for normalization
                const coords = this.originalCoordinates.map(c => c.originalCoord || c)
                this.fixedBoundingBox = this.normalizeCoordinates(coords)
            } else {
                this.selectedVertexIndex = -1
                this.selectedObstacleIndex = -1
                this.selectedObstacleVertexIndex = -1
                this.showDeleteConfirm = false
                this.vertexToDeleteIndex = -1
                this.originalCoordinates = null
                this.modifiedCoordinates = null
                this.hasChanges = false
                this.editingObstacle = false
                this.editingField = false
                // Reset the fixed bounding box
                this.fixedBoundingBox = null
            }
        },
        // Start dragging a vertex
        startDraggingVertex(index) {
            if (!this.editMode) return
            
            if (this.editingObstacle) {
                this.selectedObstacleVertexIndex = index
            } else {
                this.selectedVertexIndex = index
            }
            this.isPanning = false
        },
        // Drag a vertex to a new position
        dragVertex(event, svgRect) {
            if (!this.editMode) return
            
            const isObstacleVertex = this.editingObstacle && this.selectedObstacleVertexIndex !== -1
            const isFieldVertex = !this.editingObstacle && this.selectedVertexIndex !== -1
            
            if (!isObstacleVertex && !isFieldVertex) return
            
            const rect = svgRect.getBoundingClientRect ? svgRect.getBoundingClientRect() : svgRect
            
            // Calculate new position in SVG coordinates
            const mouseX = (event.clientX - rect.left) / rect.width * this.baseSize
            const mouseY = (event.clientY - rect.top) / rect.height * this.baseSize
            
            // Adjust for current zoom and pan
            const worldX = (mouseX / this.zoomLevel) - this.panX
            const worldY = (mouseY / this.zoomLevel) - this.panY
            
            if (isFieldVertex) {
                if (this.normalizedCoordinates[this.selectedVertexIndex]) {
                    this.normalizedCoordinates[this.selectedVertexIndex].x = worldX
                    this.normalizedCoordinates[this.selectedVertexIndex].y = worldY
                    
                    const updatedGeoCoordinates = this.svgToGeoCoordinates(worldX, worldY)
                    if (updatedGeoCoordinates) {
                        this.normalizedCoordinates[this.selectedVertexIndex].originalCoord = updatedGeoCoordinates
                    }
                    
                    this.hasChanges = true
                    this.editingField = true
                }
            } else if (isObstacleVertex) {
                const obstacle = this.normalizedObstacles[this.selectedObstacleIndex]
                if (obstacle && obstacle.coordinates[this.selectedObstacleVertexIndex]) {
                    obstacle.coordinates[this.selectedObstacleVertexIndex].x = worldX
                    obstacle.coordinates[this.selectedObstacleVertexIndex].y = worldY
                    
                    const updatedGeoCoordinates = this.svgToGeoCoordinates(worldX, worldY)
                    if (updatedGeoCoordinates) {
                        obstacle.coordinates[this.selectedObstacleVertexIndex].originalCoord = updatedGeoCoordinates
                    }
                    
                    obstacle.points = obstacle.coordinates.map(coord => `${coord.x},${coord.y}`).join(' ')
                    
                    this.hasChanges = true
                }
            }
        },
        stopDraggingVertex() {
            this.selectedVertexIndex = -1
            this.selectedObstacleVertexIndex = -1
        },
        selectObstacle(index) {
            if (!this.editMode) return
            
            this.selectedObstacleIndex = index
            this.editingObstacle = true
            this.selectedVertexIndex = -1
            
            // Backup the original obstacles
            this.obstacleBackup = JSON.parse(JSON.stringify(this.normalizedObstacles))
        },
        unselectObstacle() {
            // Save changes if there are any
            if (this.hasChanges && this.editingObstacle && this.selectedObstacleIndex !== -1) {
                this.saveObstacleChanges()
            }
            
            this.editingObstacle = false
            this.selectedObstacleIndex = -1
            this.selectedObstacleVertexIndex = -1
        },
        deleteVertex(index) {
            if (!this.editMode) return false

            if (this.editingObstacle) {
                const obstacle = this.normalizedObstacles[this.selectedObstacleIndex]
                if (!obstacle || obstacle.coordinates.length <= 3) {
                    return false
                }
                
                obstacle.coordinates.splice(index, 1)
                obstacle.points = obstacle.coordinates.map(coord => `${coord.x},${coord.y}`).join(' ')
                this.hasChanges = true
                return true
            } else {
                if (this.normalizedCoordinates.length <= 3) {
                    return false
                }
                
                this.normalizedCoordinates.splice(index, 1)
                this.hasChanges = true
                this.editingField = true
                return true
            }
        },
        cancelDelete() {
            this.showDeleteConfirm = false
            this.vertexToDeleteIndex = -1

            if (this.editingField) {
                this.editingField = false
            }
        },
        getUpdatedBorderData() {
            if (!this.hasChanges) return null
            
            const drawingStore = useDrawingStore()
            if (!drawingStore.field.outerBorder) return null
            
            // Create a copy of the current border data
            const updatedBorder = JSON.parse(JSON.stringify(
                drawingStore.field.outerBorder
            ))
            
            // Convert the normalized SVG coordinates back to geo coordinates
            const coordinates = []
            for (const coord of this.normalizedCoordinates) {
                if (!coord.originalCoord) continue
                
                // Find the original vertex from which this SVG coordinate was derived
                const originalVertex = coord.originalCoord
                
                // Use the original geo coordinates (lon/lat) but with modified positions
                coordinates.push([
                    originalVertex[0], // lon
                    originalVertex[1]  // lat
                ])
            }
            
            // Make sure polygon is closed (first and last point are the same)
            if (coordinates.length > 0 && 
                (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || 
                 coordinates[0][1] !== coordinates[coordinates.length - 1][1])) {
                coordinates.push([...coordinates[0]])
            }
            
            // If outerBorder is already a FeatureCollection (from editing)
            if (updatedBorder.type === 'FeatureCollection' && 
                updatedBorder.features && 
                updatedBorder.features[0] && 
                updatedBorder.features[0].geometry) {
                
                // Update the coordinates in the feature
                updatedBorder.features[0].geometry.coordinates[0] = coordinates
            } 
            // Otherwise it's a simple Polygon format
            else if (updatedBorder.type === 'Polygon') {
                updatedBorder.coordinates[0] = coordinates
            }
            
            return updatedBorder
        },
        svgToGeoCoordinates(svgX, svgY) {
            const original = this.originalCoordinates || this.normalizedCoordinates
            
            if (!original || original.length === 0 || !original[0].originalCoord) {
                return null
            }
            
            // Find bounding box of original coordinates
            let minLon = Infinity, maxLon = -Infinity
            let minLat = Infinity, maxLat = -Infinity
            
            for (const coord of original) {
                if (!coord.originalCoord) continue
                
                const lon = coord.originalCoord[0]
                const lat = coord.originalCoord[1]
                
                minLon = Math.min(minLon, lon)
                maxLon = Math.max(maxLon, lon)
                minLat = Math.min(minLat, lat)
                maxLat = Math.max(maxLat, lat)
            }
            
            // Find the SVG bounding box
            let minX = Infinity, maxX = -Infinity
            let minY = Infinity, maxY = -Infinity
            
            for (const coord of original) {
                minX = Math.min(minX, coord.x)
                maxX = Math.max(maxX, coord.x)
                minY = Math.min(minY, coord.y)
                maxY = Math.max(maxY, coord.y)
            }
            
            // Calculate proportions
            const lonRange = maxLon - minLon
            const latRange = maxLat - minLat
            const xRange = maxX - minX
            const yRange = maxY - minY
            
            // Ensure we don't divide by zero
            if (xRange === 0 || yRange === 0) {
                console.error('Cannot convert coordinates: SVG bounding box has zero width or height')
                return null
            }
            
            // Convert SVG coordinate to geo coordinate
            // Y is inverted in SVG (0 is top, increasing downward)
            const lon = minLon + ((svgX - minX) / xRange) * lonRange
            const lat = maxLat - ((svgY - minY) / yRange) * latRange
            
            return [lon, lat]
        },
        calculateTicks(isVertical) {
            const ticks = []
            const size = this.baseSize
            const numTicks = 10 // Always 10 ticks
            const tickSpacing = size / numTicks
            const scale = this.getScaleText
            const scaleValue = parseFloat(scale.split(' ')[0])
            const scaleUnit = scale.split(' ')[1]
            
            // Calculate exact values - each tick is exactly scaleValue/10
            const tickValue = scaleValue / numTicks
            
            for (let i = 0; i <= numTicks; i++) {
                // Calculate exact value (with one decimal precision)
                const exactValue = +(i * tickValue).toFixed(1)
                
                // Create label with unit (except for 0)
                let label = i === 0 ? '' : `${exactValue}${scaleUnit}`
                
                // For vertical axis, invert the position (0 at bottom, max at top)
                const position = isVertical ? size - i * tickSpacing : i * tickSpacing
                
                ticks.push({ position, label })
            }
            return ticks
        },
        saveObstacleChanges() {
            if (!this.hasChanges || !this.editingObstacle || this.selectedObstacleIndex === -1) {
                return false
            }
            
            const drawingStore = useDrawingStore()
            // Get the current edited obstacle
            const editedObstacle = this.normalizedObstacles[this.selectedObstacleIndex]
            // Find the corresponding obstacle in the drawing store using the ID
            const originalObstacleIndex = drawingStore.field.cutouts.findIndex(
                obstacle => obstacle.id === editedObstacle.id
            )
            
            if (originalObstacleIndex === -1) return false
            
            // Get the original obstacle
            const originalObstacle = drawingStore.field.cutouts[originalObstacleIndex]
            
            // Convert SVG coordinates back to geo coordinates
            const updatedCoordinates = []
            for (const coord of editedObstacle.coordinates) {
                if (!coord.originalCoord) continue
                
                // Use the updated coordinates from SVG
                updatedCoordinates.push({
                    lat: coord.originalCoord[1],
                    lng: coord.originalCoord[0]
                })
            }
            
            // Update the path of the Google Maps object
            if (originalObstacle && originalObstacle.getPath) {
                const path = originalObstacle.getPath()
                
                // Clear the existing path
                while (path.getLength() > 0) {
                    path.removeAt(0)
                }
                
                // Add the new coordinates
                const mapStore = useMaps()
                for (const coord of updatedCoordinates) {
                    path.push(new mapStore.google.maps.LatLng(coord.lat, coord.lng))
                }
                
                this.hasChanges = false
                
                return true
            }
            
            return false
        },
        getObstacleCenter(obstacle) {
            // If no coordinates, return default center
            if (!obstacle.coordinates || obstacle.coordinates.length === 0) {
                return { x: 0, y: 0 }
            }
            
            // Calculate average of all coordinates
            let sumX = 0
            let sumY = 0
            
            for (const coord of obstacle.coordinates) {
                sumX += coord.x
                sumY += coord.y
            }
            
            return { 
                x: sumX / obstacle.coordinates.length, 
                y: sumY / obstacle.coordinates.length 
            }
        },
        resetFixedBoundingBoxAndSVG() {
            // Reset the fixed bounding box with the new coordinates of the polygon and regenerate the SVG
            const coords = this.normalizedCoordinates.map(c => c.originalCoord || c)
            this.fixedBoundingBox = this.normalizeCoordinates(coords)
            this.generateSVG()
        }
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useSvgStore, import.meta.hot))
}